/**
 * Copyright (C) Radiojar S.A. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 *
 * Created by Tasos Kolokathis on 22/7/21.
 */

import * as _ from "lodash";
import emitter from "@/emitter";
import moment from "moment-timezone";

import PortalApi from "@/services/Portal";
import ScheduleBroadcasterAPI from "@/services/ScheduleBroadcaster";
import ServiceManagementApi from "@/services/ServiceManagement";
import StationApi from "@/services/Station";
import WebRTCService from "@/services/WebRTCService";

export default {
	state: {
		stations: {
			data: [],
			loading: false,
			favorites: [] // ids
		},

		userObjects: {
			data: [],
			loading: false
		},

		userObjectsCacheLimit: 20,
		userObjectsByFeature: {},

		loadedStations: {},
		selectedStations: [],

		liveModeSelectedStationID: null,

		isMidiOpen: false
	},

	getters: {
		getStations: state => () => {
			return {
				...state.stations,
				loading: state.stations.loading || state.userObjects.loading
			};
		},

		getUserObjects: state => () => {
			return state.userObjects;
		},

		getUserObjectsCacheLimit: state => () => {
			return state.userObjectsCacheLimit;
		},

		getLoadedStationById: state => stationID => {
			return state.loadedStations[stationID];
		},

		liveModeSelectedStationID: state => () => {
			return state.liveModeSelectedStationID;
		},

		liveModeSelectedStation: state => () => {
			if (!state.liveModeSelectedStationID) {
				return null;
			}
			return state.loadedStations[state.liveModeSelectedStationID];
		},

		getSelectedStations: state => () => {
			return state.selectedStations;
		},

		getStationById: state => stationId => {
			return state.stations.data.find(({StationID}) => StationID === stationId);
		},

		getIsMidiOpen: state => () => {
			return state.isMidiOpen;
		}
	},

	mutations: {
		appendStations(state, stations) {
			stations.forEach(station => {
				if (!state.stations.data.some(s => s.id === station.id)) {
					state.stations.data.push(station);
				}
			});
		},

		setStationsLoading(state, loading) {
			state.stations.loading = loading;
		},

		setFavoriteStations(state, stations) {
			state.stations.favorites = stations;
		},

		setUserObjects(state, objects) {
			state.userObjects.data = objects;
		},

		setUserObjectsLoading(state, loading) {
			state.userObjects.loading = loading;
		},

		resetUserObjects(state) {
			state.userObjects = {
				data: [],
				loading: false
			};
		},

		setUserObjectsByFeature(state, {featureRef, objectIds}) {
			state.userObjectsByFeature[featureRef] = objectIds;
		},

		addLoadedStation(state, station) {
			if (!state.loadedStations[station.StationID]) {
				state.loadedStations[station.StationID] = station;
			}
		},

		setLiveModeSelectedStationID(state, stationID) {
			state.liveModeSelectedStationID = stationID;
		},

		resetLoadedStations(state) {
			state.loadedStations = {};
		},

		resetLiveModeSelectedStationID(state) {
			state.liveModeSelectedStationID = null;
		},

		resetStationState(state) {
			state.stations = {
				data: [],
				loading: false,
				favorites: [] // ids
			};
			state.loadedStations = {};
			state.liveModeSelectedStationID = null;
		},

		updateLoadedStation(state, updatedData) {
			const loadedStation = state.loadedStations[updatedData.StationID];

			if (loadedStation) {
				// Update each property individually to keep reactivity intact
				for (let key in updatedData) {
					loadedStation[key] = updatedData[key];
				}
			}
		},

		addSelectedStations(state, station) {
			if (!state.selectedStations.some(s => s.StationID === station.StationID)) {
				state.selectedStations.push(station);
			} else {
				state.selectedStations.find(s => s.StationID === station.StationID).count++;
			}
		},

		removeSelectedStation(state, stationID) {
			const stationIndex = state.selectedStations.findIndex(s => s.StationID === stationID);
			if (stationIndex > -1) {
				if (state.selectedStations[stationIndex].count > 1) {
					state.selectedStations[stationIndex].count--;
				} else {
					state.selectedStations.splice(stationIndex, 1);
				}
			}
		},

		setIsMidiOpen(state, isMidiOpen) {
			state.isMidiOpen = isMidiOpen;
		}
	},

	actions: {
		getUserObjects({getters, commit}) {
			commit("setUserObjectsLoading", true);
			return ServiceManagementApi.getUserObjects()
				.then(response => {
					const userObjects = _.map(response.data, "object_id");
					commit("setUserObjects", userObjects);
					emitter.emit("user-objects-loaded");

					// if a user has no access to the account and user objects are <= userObjectsCacheLimit, preload the stations list
					if (
						!userObjects.includes(getters.getOrgID()) &&
						userObjects.length <= getters.getUserObjectsCacheLimit()
					) {
						emitter.emit("preload-user-stations");
					}
				})
				.finally(() => {
					commit("setUserObjectsLoading", false);
				});
		},

		async getObjectsForFeature({commit, state}, featureRef) {
			if (state.userObjectsByFeature[featureRef]) {
				return state.userObjectsByFeature[featureRef];
			}
			return ServiceManagementApi.getUserObjects(featureRef).then(response => {
				const objectIds = _.map(response.data, "object_id");
				commit("setUserObjectsByFeature", {featureRef, objectIds});
				return objectIds;
			});
		},

		async searchStations({getters, commit, dispatch}, params = {search: "", limit: 20, offset: 0, feature: null}) {
			commit("setStationsLoading", true);
			const userObjects = getters.getUserObjects();

			let stations = [];
			let filteredStations = [];
			let hasMore = false;

			let featureEnabledObjectIds = [];
			if (params.feature) {
				featureEnabledObjectIds = await dispatch("getObjectsForFeature", params.feature);
			}

			if (!userObjects.loading) {
				if (userObjects.data.includes(getters.getOrgID())) {
					// if the user has access to the account
					let currentOffset = params.offset;
					while (filteredStations.length < params.limit) {
						stations = await StationApi.getUserStations({
							// search for account stations
							..._.omit(params, ["feature"]),
							offset: currentOffset
						})
							.then(async response => {
								const stationsFromApi = response.data;
								if (stationsFromApi.length === 0) {
									return [];
								}
								currentOffset += stationsFromApi.length;

								if (params.feature) {
									return stationsFromApi.reduce((acc, station) => {
										if (featureEnabledObjectIds.includes(station.id)) {
											acc.push({
												...station,
												StationID: station.id,
												StationName: station.name,
												Logo: `${station.logo}?${moment().valueOf()}`
											});
										}
										return acc;
									}, []);
								} else {
									return stationsFromApi.map(station => ({
										...station,
										StationID: station.id,
										StationName: station.name,
										Logo: `${station.logo}?${moment().valueOf()}`
									}));
								}
							})
							.catch(() => []);

						if (stations.length === 0) {
							break;
						}

						commit("appendStations", stations);
						stations.forEach(station => {
							if (!filteredStations.some(s => s.StationID === station.StationID)) {
								filteredStations.push(station);
							}
						});
					}

					filteredStations = filteredStations.slice(0, params.limit);
					hasMore = filteredStations.length >= params.limit;
					params.offset = currentOffset;
				} else {
					if (getters.getStations().data.length === 0) {
						// If no stations, fetch user's entities
						stations = await PortalApi.getEntities(userObjects.data)
							.then(response => {
								return response.data.stations.reduce((acc, station) => {
									//keep only stations under default account and fix stations' format
									if (station.base.account_id === getters.getOrgID()) {
										acc.push({
											...station.base,
											...station.extended,
											StationID: station.base.id,
											StationName: station.name,
											Logo: `${station.extended.logo}?${moment().valueOf()}`
										});
									}
									return acc;
								}, []);
							})
							.catch(() => []);

						commit("appendStations", stations);
					} else {
						//else get the already fetched stations
						stations = getters.getStations().data;
					}

					/*
					 * filter stations according to the user's search term
					 * (check if any of the "name", "call_letters" or "streamname" attribute values contain the search term)
					 * and return a sub-array according to the param's limit and offset values
					 * */
					filteredStations = stations
						.filter(station => {
							return Object.keys(station).some(key => {
								return (
									["name", "call_letters", "streamname"].includes(key) &&
									station[key]?.toLowerCase().includes(params.search.toLowerCase())
								);
							});
						})
						.slice(params.offset, params.offset + params.limit);
					params.offset += filteredStations.length;
					hasMore = filteredStations.length === params.limit;
				}
			}
			return new Promise(resolve => {
				commit("setStationsLoading", false);
				return resolve({filteredStations, hasMore, offset: params.offset});
			});
		},

		async getStations({commit, dispatch}, params) {
			let featureEnabledObjectIds = [];
			if (params.feature) {
				featureEnabledObjectIds = await dispatch("getObjectsForFeature", params.feature);
			}

			return StationApi.getUserStations(params)
				.then(async response => {
					const stationsFromApi = response.data;
					if (params.feature) {
						const filteredStations = stationsFromApi.reduce((acc, station) => {
							if (featureEnabledObjectIds.includes(station.id)) {
								acc.push({
									...station,
									StationID: station.id,
									StationName: station.name,
									Logo: `${station.logo}?${moment().valueOf()}`
								});
							}
							return acc;
						}, []);

						commit("appendStations", filteredStations);
						return filteredStations;
					} else {
						const formattedStations = stationsFromApi.map(station => ({
							...station,
							StationID: station.id,
							StationName: station.name,
							Logo: `${station.logo}?${moment().valueOf()}`
						}));
						commit("appendStations", formattedStations);
						return formattedStations;
					}
				})
				.catch(() => []);
		},

		async getStation({getters, commit}, s) {
			const station =
				getters.getLoadedStationById(s.StationID) ||
				(await StationApi.get(s.StationID).then(response => ({...s, ...response.data})));

			commit("addLoadedStation", station);

			return station;
		},

		getFavoriteStations({commit}) {
			return StationApi.getUserFavoriteStations().then(response => {
				const favoriteStations = response.data.stations;
				commit("setFavoriteStations", response.data.stations);
				return favoriteStations;
			});
		},

		createRecordingSession({getters, commit}) {
			const studio = getters.getStudioSettings().reference;
			const stationID = getters.liveModeSelectedStationID();
			const fullname = getters.getUserFullName();
			const now = moment();
			let payload = {
				title: `Live Show from ${studio} at ${stationID} - ${now.toISOString()}`,
				artist: fullname,
				start: now.valueOf(),
				stop: now.add(24, "h").valueOf(),
				context_data: {live_studio: studio, talent: fullname}
			};
			return ScheduleBroadcasterAPI.createRecordingSession(payload)
				.then(response => response.data)
				.catch(() => {
					commit("SHOW_NOTIFICATION_WITH_TYPE", {
						message: "An error occurred when trying to create Live Session."
					});
				});
		},

		connectStudio({getters, dispatch}, role) {
			if (getters.isWebRTCStudio()) {
				return dispatch("openWebRTCConnection", role);
			} else {
				dispatch("playerSIPConnectRequest", role);
				return dispatch("getWebRTCToken")
					.then(() => {
						WebRTCService.initialize();
						return WebRTCService.openConnection(role);
					})
					.catch(error => {
						console.error("connectStudio error: ", error);
						return Promise.reject(error);
					});
			}
		},

		deleteRecordingSession(store, {recordingSession, afterDuration}) {
			// add 600 seconds to the duration
			afterDuration = parseInt(afterDuration, 10) + 600;
			return ScheduleBroadcasterAPI.deleteRecordingSession(recordingSession, afterDuration);
		},

		disconnectStudio({getters, dispatch}, role) {
			if (getters.isWebRTCStudio()) {
				dispatch("closeWebRTCConnection");
				localStorage.removeItem("faderValues");
			} else {
				dispatch("playerSIPDisconnectRequest", role);
			}
		},

		async sendHeartbeat(state, payload) {
			await StationApi.postHeartbeat(payload);
		},

		getTrafficLog({commit}, {station, startTime, breakDuration}) {
			const stationEnterpriseID = station.ThirdPartyIDs?.IHM_EnterpriseID;
			if (stationEnterpriseID && !_.isNaN(parseInt(stationEnterpriseID))) {
				return StationApi.getTrafficLog(stationEnterpriseID, startTime, breakDuration);
			} else {
				commit("SHOW_NOTIFICATION_WITH_TYPE", {
					message: "Station doesn't contain Enterprise ID and could not fetch traffic."
				});
			}
		},

		getStationsByOrgID() {
			return StationApi.getStationsByOrgID();
		},

		getStationsBySyndicationType(state, type) {
			return StationApi.getStationsBySyndicationType(type)
				.then(response => response.data.stations)
				.catch(() => []);
		},

		updateStation({commit}, {stationID, payload}) {
			return StationApi.updateStation({stationID, payload}).then(response => {
				commit("updateLoadedStation", response.data);
				return response;
			});
		}
	}
};
